import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Auth endpoints
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
};

// User endpoints
export const userAPI = {
  getProfile: () => api.get('/users/profile'),
  updateProfile: (data) => api.put('/users/profile', data),
  getAmbassadors: (params) => api.get('/users/ambassadors', { params }),
};

// Like endpoints
export const likeAPI = {
  createLike: (ambassadorId, demoMode) => api.post('/likes', { ambassadorId, demoMode }),
  createPass: (ambassadorId) => api.post('/likes/pass', { ambassadorId }),
  declineLike: (brandId) => api.post('/likes/decline', { brandId }),
  demoAcceptLike: (ambassadorId) => api.post('/likes/demo-accept', { ambassadorId }),
  getReceivedLikes: () => api.get('/likes/received'),
};

// Match endpoints
export const matchAPI = {
  createMatch: (brandId, demoMode) => api.post('/matches', { brandId, demoMode }),
  getMatches: () => api.get('/matches'),
};

// Message endpoints
export const messageAPI = {
  getMessages: (matchId) => api.get(`/messages/${matchId}`),
  createMessage: (matchId, content) => api.post('/messages', { matchId, content }),
};

// Booking endpoints
export const bookingAPI = {
  createBooking: (data) => api.post('/bookings', data),
  getBookings: () => api.get('/bookings'),
  getBookingById: (id) => api.get(`/bookings/${id}`),
  updateBookingStatus: (id, status) => api.put(`/bookings/${id}/status`, { status }),
  updateBookingTimes: (id, data) => api.put(`/bookings/${id}/times`, data),
  duplicateBooking: (id) => api.post(`/bookings/${id}/duplicate`),
  updateDraftBooking: (id, data) => api.put(`/bookings/${id}/draft`, data),
  sendDraftBooking: (id) => api.post(`/bookings/${id}/send`),
  deleteBooking: (id) => api.delete(`/bookings/${id}`),
  checkIn: (id) => api.post(`/bookings/${id}/check-in`),
  checkOut: (id) => api.post(`/bookings/${id}/check-out`),
  getTimeStatus: (id) => api.get(`/bookings/${id}/time-status`),
};

// Line item endpoints (commissions, reimbursements)
export const lineItemAPI = {
  getLineItems: () => api.get('/line-items'),
  createLineItem: (data) => api.post('/line-items', data),
  updateLineItem: (id, data) => api.put(`/line-items/${id}`, data),
  deleteLineItem: (id) => api.delete(`/line-items/${id}`),
};

// Internal hours endpoints (admin only — manually-tracked staff hours)
export const internalHoursAPI = {
  getInternalHours: () => api.get('/internal-hours'),
  createInternalHours: (data) => api.post('/internal-hours', data),
  updateInternalHours: (id, data) => api.put(`/internal-hours/${id}`, data),
  deleteInternalHours: (id) => api.delete(`/internal-hours/${id}`),
};

// Admin endpoints
export const adminAPI = {
  searchUsers: (query) => api.get('/admin/users/search', { params: { q: query } }),
  getAllUsersWithStatus: (role) => api.get('/admin/users/status', { params: { role } }),
  impersonateUser: (userId) => api.post('/admin/impersonate', { userId }),
  stopImpersonation: () => api.post('/admin/stop-impersonation'),
  resetDemoData: (targetUserId) => api.post('/admin/reset-demo-data', { targetUserId }),
};

// Review endpoints
export const reviewAPI = {
  createReview: (data) => api.post('/reviews', data),
  getBookingReviews: (bookingId) => api.get(`/reviews/booking/${bookingId}`),
  getUserReviews: (userId) => api.get(`/reviews/user/${userId}`),
  getBookingsNeedingReview: () => api.get('/reviews/needs-review'),
};

// Preview endpoints
export const previewAPI = {
  resetPreview: () => api.post('/preview/reset'),
  togglePreviewAmbassador: (ambassadorId, enabled) => api.post('/preview/toggle-ambassador', { ambassadorId, enabled }),
  generateBrandMessage: (matchId, demoMode) => api.post('/preview/generate-brand-message', { matchId, demoMode }),
  generateEventDetails: () => api.post('/preview/generate-event-details'),
};

// SMS Campaign endpoints (admin only)
export const smsCampaignAPI = {
  list: () => api.get('/sms-campaigns'),
  create: (data) => api.post('/sms-campaigns', data),
  get: (id) => api.get(`/sms-campaigns/${id}`),
  update: (id, data) => api.patch(`/sms-campaigns/${id}`, data),
  listSubmissions: (id, params) => api.get(`/sms-campaigns/${id}/submissions`, { params }),
  exportCsvUrl: (id) => `${API_URL}/sms-campaigns/${id}/export`,
  getCouponPool: (id) => api.get(`/sms-campaigns/${id}/coupons`),
  uploadCoupons: (id, codes) => api.post(`/sms-campaigns/${id}/coupons`, { codes }),
  manualAssign: (id, data) => api.post(`/sms-campaigns/${id}/manual-assign`, data),
};

// Inquiry endpoints (broadcast availability checks)
export const inquiryAPI = {
  createInquiry: (data) => api.post('/inquiries', data),
  getInquiries: () => api.get('/inquiries'),
  getInquiryResponses: (inquiryId) => api.get(`/inquiries/${inquiryId}/responses`),
  respondToInquiry: (inquiryId, response) => api.post(`/inquiries/${inquiryId}/respond`, { response }),
  selectAmbassador: (inquiryId, ambassadorId) => api.post(`/inquiries/${inquiryId}/select`, { ambassadorId }),
  cancelInquiry: (inquiryId) => api.delete(`/inquiries/${inquiryId}`),
};

export default api;
